/**
 * Client-side JPEG-LS -> uncompressed pre-transcode.
 *
 * dwv 0.36.4 (the DICOM rendering library this viewer is built on — see
 * DicomViewer.tsx) has ZERO support for JPEG-LS anywhere in its shipped
 * code: its transfer-syntax whitelist, its decoder dispatch (a hardcoded
 * if/else over exactly 4 codec names — jpeg-baseline, jpeg-lossless
 * [classic Process 14, NOT JPEG-LS], jpeg2000, rle), and its worker scripts
 * all have no plugin/registry/extension point (confirmed by reading dwv's
 * actual shipped `dist/dwv.d.ts` — there is no decoder-registration
 * export). Patching dwv's own internals was ruled out: it would silently
 * break on every dwv upgrade.
 *
 * Instead, THIS module runs before dwv ever sees the file's bytes:
 *  1. Read just the File Meta Information group (a small, fixed, always
 *     Explicit-VR-Little-Endian structure — see DICOM PS3.10 Section 7.1)
 *     to get the TransferSyntaxUID, tag (0002,0010).
 *  2. If it is NOT one of the two JPEG-LS transfer syntaxes
 *     ('1.2.840.10008.1.2.4.80' lossless / '.81' near-lossless), do
 *     nothing — return the original bytes untouched. This is the gate that
 *     keeps every other transfer syntax exactly as fast/unaffected as
 *     before this module existed.
 *  3. Otherwise, parse just enough of the dataset (Rows, Columns,
 *     BitsAllocated, PixelRepresentation, SamplesPerPixel,
 *     PlanarConfiguration, NumberOfFrames, and the PixelData element's
 *     encapsulated item/fragment structure) to decode every frame's
 *     compressed pixel data with `@cornerstonejs/codec-charls` (a
 *     maintained WASM build of the reference CharLS JPEG-LS codec — the
 *     same one Cornerstone3D uses), then rebuild the DICOM byte stream:
 *     TransferSyntaxUID rewritten to Explicit VR Little Endian
 *     ('1.2.840.10008.1.2.1' — the same "uncompressed" convention already
 *     used by MedSyncIQBackend's own DICOM test fixtures), the encapsulated
 *     PixelData element replaced by a flat OB/OW element holding the raw
 *     decoded bytes, and the two length fields that change as a result
 *     (the file meta group's own group-length element, and PixelData's own
 *     element length) fixed up to match.
 *
 * From dwv's perspective, the rebuilt bytes are then a perfectly ordinary,
 * uncompressed DICOM file — it never knows JPEG-LS was ever involved.
 *
 * A hand-rolled reader/writer (rather than dwv's own exported `DicomParser`
 * / `DicomWriter`) was a deliberate choice: dwv DOES export those (checked
 * `dist/dwv.d.ts` first, per this task's own instruction to reuse a
 * lower-level parsing utility if one exists), but `DataElement`'s
 * encapsulated-sequence `items` shape is untyped/undocumented in the
 * public API, and this module needs exact byte offsets to splice the
 * original buffer surgically rather than re-serializing the whole file —
 * something dwv's own writer API gives no documented control over. The
 * structure actually needed here (Explicit VR LE tag/VR/length walking,
 * plus the standard Item/fragment encapsulation format used by ALL
 * compressed transfer syntaxes, not just JPEG-LS) is small, standard, and
 * fully spec-driven — see DICOM PS3.5 Section 7.1.2 (VR length forms) and
 * Annex A.4 (encapsulated pixel data).
 */

// ---- Transfer syntax UIDs ----

/** JPEG-LS Lossless Image Compression. */
export const JPEG_LS_LOSSLESS_UID = "1.2.840.10008.1.2.4.80";
/** JPEG-LS Lossy (Near-Lossless) Image Compression — same codec family. */
export const JPEG_LS_NEAR_LOSSLESS_UID = "1.2.840.10008.1.2.4.81";

const JPEG_LS_TRANSFER_SYNTAXES: ReadonlySet<string> = new Set([
  JPEG_LS_LOSSLESS_UID,
  JPEG_LS_NEAR_LOSSLESS_UID,
]);

/** Explicit VR Little Endian — the uncompressed transfer syntax this module
 *  rewrites JPEG-LS files to. Matches the convention MedSyncIQBackend's own
 *  DICOM test fixtures already use for "uncompressed" (see
 *  `tests/imaging_studies/test_dicom_utils.py`). */
export const UNCOMPRESSED_TRANSFER_SYNTAX_UID = "1.2.840.10008.1.2.1";

export function isJpegLsTransferSyntax(uid: string): boolean {
  return JPEG_LS_TRANSFER_SYNTAXES.has(uid);
}

// ---- Low-level byte structure helpers ----

const PREAMBLE_LENGTH = 128;
const DICM_MAGIC = "DICM";

/** DICOM PS3.10 7.1: 128-byte preamble, then the 4-byte 'DICM' magic. */
export function hasDicomMagic(bytes: Uint8Array): boolean {
  if (bytes.length < PREAMBLE_LENGTH + 4) return false;
  for (let i = 0; i < 4; i++) {
    if (bytes[PREAMBLE_LENGTH + i] !== DICM_MAGIC.charCodeAt(i)) return false;
  }
  return true;
}

/** VRs whose length is a plain 2-byte field directly after the 2 VR
 *  characters. Every other VR (OB, OW, OF, SQ, UC, UR, UT, UN, ...) uses 2
 *  reserved bytes followed by a 4-byte length instead — DICOM PS3.5
 *  Section 7.1.2. */
const SHORT_VR_LENGTH = new Set([
  "AE", "AS", "AT", "CS", "DA", "DS", "DT", "FL", "FD", "IS", "LO", "LT",
  "PN", "SH", "SL", "SS", "ST", "TM", "UI", "UL", "US",
]);

/** Sentinel value for an element/item whose length is not given up front
 *  (a sequence, or encapsulated pixel data) — DICOM PS3.5 7.1.2. */
const UNDEFINED_LENGTH = 0xffffffff;

const TAG_ITEM = tagNum(0xfffe, 0xe000);
const TAG_ITEM_DELIMITATION = tagNum(0xfffe, 0xe00d);
const TAG_SEQUENCE_DELIMITATION = tagNum(0xfffe, 0xe0dd);

const TAG_FILE_META_GROUP_LENGTH = tagNum(0x0002, 0x0000);
const TAG_TRANSFER_SYNTAX_UID = tagNum(0x0002, 0x0010);

const TAG_SAMPLES_PER_PIXEL = tagNum(0x0028, 0x0002);
const TAG_PLANAR_CONFIGURATION = tagNum(0x0028, 0x0006);
const TAG_NUMBER_OF_FRAMES = tagNum(0x0028, 0x0008);
const TAG_ROWS = tagNum(0x0028, 0x0010);
const TAG_COLUMNS = tagNum(0x0028, 0x0011);
const TAG_BITS_ALLOCATED = tagNum(0x0028, 0x0100);
const TAG_PIXEL_REPRESENTATION = tagNum(0x0028, 0x0103);
const TAG_PIXEL_DATA = tagNum(0x7fe0, 0x0010);

function tagNum(group: number, element: number): number {
  return (group << 16) | element;
}

interface ElementHeader {
  tag: number;
  vr: string;
  /** Offset of the element's tag (start of the whole element). */
  start: number;
  /** Offset of the first value byte. */
  valueOffset: number;
  /** Value length in bytes, or UNDEFINED_LENGTH for a sequence/encapsulated
   *  pixel data whose end is only known once its delimiter is found. */
  length: number;
}

function readElementHeader(view: DataView, offset: number): ElementHeader {
  const group = view.getUint16(offset, true);
  const element = view.getUint16(offset + 2, true);
  const vr = String.fromCharCode(view.getUint8(offset + 4), view.getUint8(offset + 5));
  if (SHORT_VR_LENGTH.has(vr)) {
    return {
      tag: tagNum(group, element),
      vr,
      start: offset,
      valueOffset: offset + 8,
      length: view.getUint16(offset + 6, true),
    };
  }
  return {
    tag: tagNum(group, element),
    vr,
    start: offset,
    valueOffset: offset + 12,
    length: view.getUint32(offset + 8, true),
  };
}

/** Offset right after an element's value, given its header — for a
 *  defined-length element this is trivial; for an undefined-length one
 *  (always a sequence, or PixelData itself) the caller must instead walk
 *  forward to find the matching delimiter (see `skipUndefinedLengthValue`
 *  and `readPixelDataFragments`). */
function definedEnd(header: ElementHeader): number {
  return header.valueOffset + header.length;
}

/** Skips over a sequence's (or any undefined-length element's) value by
 *  walking its Item structure, WITHOUT interpreting the items' contents —
 *  a defined-length item is skipped in one jump; an undefined-length item
 *  (a nested nested dataset with its own Item Delimitation Item) is walked
 *  generically, recursing into any further nested sequences it contains.
 *  Returns the offset right after the terminating Sequence Delimitation
 *  Item. */
function skipUndefinedLengthValue(view: DataView, valueOffset: number): number {
  let pos = valueOffset;
  for (;;) {
    const group = view.getUint16(pos, true);
    const element = view.getUint16(pos + 2, true);
    const t = tagNum(group, element);
    if (t === TAG_SEQUENCE_DELIMITATION) {
      return pos + 8; // tag (4) + length (4, always 0)
    }
    if (t !== TAG_ITEM) {
      throw new Error(
        `Malformed DICOM: expected Item or Sequence Delimitation Item at offset ${pos}, found tag ${t.toString(16)}`,
      );
    }
    const itemLength = view.getUint32(pos + 4, true);
    if (itemLength === UNDEFINED_LENGTH) {
      pos = skipUndefinedLengthItemContents(view, pos + 8);
    } else {
      pos = pos + 8 + itemLength;
    }
  }
}

/** Walks the elements inside an undefined-length Item until its Item
 *  Delimitation Item, recursing into any nested sequences found along the
 *  way. Only reachable for pathological/nested structures ahead of
 *  PixelData — not exercised by this app's own fixtures, but implemented
 *  for correctness rather than assuming it never happens. */
function skipUndefinedLengthItemContents(view: DataView, start: number): number {
  let pos = start;
  for (;;) {
    const group = view.getUint16(pos, true);
    const element = view.getUint16(pos + 2, true);
    const t = tagNum(group, element);
    if (t === TAG_ITEM_DELIMITATION) {
      return pos + 8;
    }
    const header = readElementHeader(view, pos);
    pos = header.length === UNDEFINED_LENGTH ? skipUndefinedLengthValue(view, header.valueOffset) : definedEnd(header);
  }
}

// ---- File Meta group parsing (step 1: detection) ----

export interface FileMetaInfo {
  transferSyntaxUid: string;
  /** Offset of the File Meta Group Length element's VALUE (the 4-byte
   *  number itself), needed later to patch it in place. */
  groupLengthValueOffset: number;
  /** The parsed group length value (bytes remaining in the meta group
   *  after the group-length element itself). */
  groupLengthValue: number;
  /** Byte range of the whole TransferSyntaxUID element (tag through end of
   *  value), needed later to splice a replacement in. */
  transferSyntaxElementStart: number;
  transferSyntaxElementEnd: number;
  /** Offset where the main dataset begins (right after the meta group). */
  datasetStart: number;
}

function readAsciiTrimmed(view: DataView, offset: number, length: number): string {
  let s = "";
  for (let i = 0; i < length; i++) s += String.fromCharCode(view.getUint8(offset + i));
  // DICOM string VRs pad with a trailing space or NUL to keep values
  // even-length — strip both, plus any incidental surrounding whitespace.
  return s.replace(/[\0 ]+$/, "");
}

/**
 * Reads ONLY the File Meta Information group far enough to get the
 * TransferSyntaxUID — the cheap, fast-path check every DICOM file goes
 * through before Step 1 hands its bytes to dwv. Returns `null` (rather than
 * throwing) for anything that doesn't look like a well-formed DICOM file
 * meta group, so callers can safely fall back to "hand dwv the original
 * bytes, let dwv's own error handling take it from here" exactly as before
 * this module existed.
 */
export function readFileMeta(bytes: Uint8Array): FileMetaInfo | null {
  try {
    if (!hasDicomMagic(bytes)) return null;
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const metaStart = PREAMBLE_LENGTH + 4;

    const groupLengthHeader = readElementHeader(view, metaStart);
    if (groupLengthHeader.tag !== TAG_FILE_META_GROUP_LENGTH) return null;
    const groupLengthValue = view.getUint32(groupLengthHeader.valueOffset, true);
    const groupLengthValueOffset = groupLengthHeader.valueOffset;
    const metaGroupEnd = definedEnd(groupLengthHeader) + groupLengthValue;
    const datasetStart = metaGroupEnd;

    let pos = definedEnd(groupLengthHeader);
    let transferSyntaxUid: string | null = null;
    let transferSyntaxElementStart = -1;
    let transferSyntaxElementEnd = -1;
    while (pos < metaGroupEnd) {
      const header = readElementHeader(view, pos);
      const end = header.length === UNDEFINED_LENGTH ? skipUndefinedLengthValue(view, header.valueOffset) : definedEnd(header);
      if (header.tag === TAG_TRANSFER_SYNTAX_UID) {
        transferSyntaxUid = readAsciiTrimmed(view, header.valueOffset, header.length);
        transferSyntaxElementStart = header.start;
        transferSyntaxElementEnd = end;
        break;
      }
      pos = end;
    }
    if (transferSyntaxUid === null) return null;

    return {
      transferSyntaxUid,
      groupLengthValueOffset,
      groupLengthValue,
      transferSyntaxElementStart,
      transferSyntaxElementEnd,
      datasetStart,
    };
  } catch {
    return null;
  }
}

// ---- Dataset parsing (step 2: only for confirmed JPEG-LS files) ----

interface PixelDataFragment {
  offset: number;
  length: number;
}

interface DatasetPixelInfo {
  rows: number;
  columns: number;
  bitsAllocated: number;
  pixelRepresentation: number;
  samplesPerPixel: number;
  planarConfiguration: number;
  numberOfFrames: number;
  pixelDataElementStart: number;
  /** Offset right after the PixelData element's terminating Sequence
   *  Delimitation Item — where any bytes following pixel data begin. */
  pixelDataRunEnd: number;
  fragments: PixelDataFragment[];
}

/**
 * Walks the main dataset (Explicit VR Little Endian — guaranteed for any
 * JPEG-LS transfer syntax by the DICOM standard itself, so this module
 * never needs to support Implicit VR or Big Endian datasets) from
 * `datasetStart` up to and including the PixelData element, collecting the
 * handful of tags needed to decode it plus its encapsulated fragment
 * layout.
 */
export function readDatasetPixelInfo(bytes: Uint8Array, datasetStart: number): DatasetPixelInfo {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let pos = datasetStart;

  let rows: number | undefined;
  let columns: number | undefined;
  let bitsAllocated: number | undefined;
  let pixelRepresentation = 0;
  let samplesPerPixel = 1;
  let planarConfiguration = 0;
  let numberOfFrames = 1;

  for (;;) {
    if (pos >= bytes.byteLength) {
      throw new Error("Malformed DICOM: reached end of file before finding PixelData.");
    }
    const header = readElementHeader(view, pos);

    if (header.tag === TAG_PIXEL_DATA) {
      if (rows === undefined || columns === undefined || bitsAllocated === undefined) {
        throw new Error("Malformed DICOM: PixelData found before Rows/Columns/BitsAllocated were read.");
      }
      if (header.length !== UNDEFINED_LENGTH) {
        throw new Error(
          "Unsupported: PixelData for a JPEG-LS transfer syntax must be encapsulated (undefined length), " +
            "but this file's PixelData has a defined length — the file is likely malformed or misdeclared.",
        );
      }
      const { fragments, end } = readPixelDataFragments(view, header.valueOffset);
      return {
        rows,
        columns,
        bitsAllocated,
        pixelRepresentation,
        samplesPerPixel,
        planarConfiguration,
        numberOfFrames,
        pixelDataElementStart: header.start,
        pixelDataRunEnd: end,
        fragments,
      };
    }

    switch (header.tag) {
      case TAG_ROWS:
        rows = view.getUint16(header.valueOffset, true);
        break;
      case TAG_COLUMNS:
        columns = view.getUint16(header.valueOffset, true);
        break;
      case TAG_BITS_ALLOCATED:
        bitsAllocated = view.getUint16(header.valueOffset, true);
        break;
      case TAG_PIXEL_REPRESENTATION:
        pixelRepresentation = view.getUint16(header.valueOffset, true);
        break;
      case TAG_SAMPLES_PER_PIXEL:
        samplesPerPixel = view.getUint16(header.valueOffset, true);
        break;
      case TAG_PLANAR_CONFIGURATION:
        planarConfiguration = view.getUint16(header.valueOffset, true);
        break;
      case TAG_NUMBER_OF_FRAMES: {
        const str = readAsciiTrimmed(view, header.valueOffset, header.length);
        const parsed = parseInt(str, 10);
        if (Number.isFinite(parsed) && parsed > 0) numberOfFrames = parsed;
        break;
      }
      default:
        break;
    }

    pos = header.length === UNDEFINED_LENGTH ? skipUndefinedLengthValue(view, header.valueOffset) : definedEnd(header);
  }
}

/** Reads an encapsulated PixelData element's Item structure: a first item
 *  (the Basic Offset Table — its contents are not needed here, only its
 *  length, to skip past it) followed by one fragment Item per subsequent
 *  item, terminated by a Sequence Delimitation Item. DICOM PS3.5 Annex A.4. */
function readPixelDataFragments(
  view: DataView,
  valueOffset: number,
): { fragments: PixelDataFragment[]; end: number } {
  let pos = valueOffset;

  // Basic Offset Table — first item, always present (may be empty).
  const botGroup = view.getUint16(pos, true);
  const botElement = view.getUint16(pos + 2, true);
  if (tagNum(botGroup, botElement) !== TAG_ITEM) {
    throw new Error("Malformed DICOM: encapsulated PixelData missing its Basic Offset Table item.");
  }
  const botLength = view.getUint32(pos + 4, true);
  pos += 8 + botLength;

  const fragments: PixelDataFragment[] = [];
  for (;;) {
    const group = view.getUint16(pos, true);
    const element = view.getUint16(pos + 2, true);
    const t = tagNum(group, element);
    if (t === TAG_SEQUENCE_DELIMITATION) {
      pos += 8;
      break;
    }
    if (t !== TAG_ITEM) {
      throw new Error(`Malformed DICOM: expected a PixelData fragment Item at offset ${pos}.`);
    }
    const length = view.getUint32(pos + 4, true);
    if (length === UNDEFINED_LENGTH) {
      throw new Error("Malformed DICOM: a PixelData fragment item cannot itself have undefined length.");
    }
    fragments.push({ offset: pos + 8, length });
    pos += 8 + length;
  }

  return { fragments, end: pos };
}

/** Groups a flat fragment list into one contiguous byte range per frame.
 *  The overwhelmingly common convention (and what this module's own test
 *  fixtures produce) is exactly one fragment per frame; the other
 *  supported shape is a single frame split across several fragments. A
 *  mixed/ambiguous fragmentation (multiple frames, uneven fragment counts)
 *  would require parsing the Basic Offset Table's actual byte offsets to
 *  disambiguate, which real-world JPEG-LS DICOM encoders essentially never
 *  produce — rather than silently guessing wrong, this throws a clear
 *  error surfaced through the same decode-error UI path as any other
 *  unsupported file. */
function groupFragmentsIntoFrames(fragments: PixelDataFragment[], numberOfFrames: number): PixelDataFragment[][] {
  if (fragments.length === numberOfFrames) {
    return fragments.map((f) => [f]);
  }
  if (numberOfFrames === 1) {
    return [fragments];
  }
  throw new Error(
    `Unsupported PixelData fragmentation: ${fragments.length} fragment(s) for ${numberOfFrames} frame(s).`,
  );
}

// ---- CharLS WASM decoder loading ----

/** Mirrors this codebase's existing pattern for dwv's own decode web
 *  workers (copied into `public/assets/workers/` and referenced by a
 *  runtime URL, rather than left to relative-to-module-URL resolution that
 *  Vite's bundling breaks — see DicomViewer.tsx's module docstring): the
 *  CharLS WASM binary is copied into `public/assets/wasm/` at
 *  `charls-decode.wasm` and loaded via an explicit root-relative URL passed
 *  through the module's own `locateFile` hook, instead of relying on
 *  import.meta.url-relative resolution inside the third-party glue code. */
let charlsWasmUrl = "/assets/wasm/charls-decode.wasm";

/** Test-only seam: lets the unit test suite point the WASM loader at the
 *  real file on disk (a `file`-system path resolved via Node's own module
 *  resolution) instead of the browser-only root-relative URL used above —
 *  a plain root-relative string obviously can't resolve under Node/Vitest.
 *  Never called from application code. Resets the cached module promise so
 *  the next decode picks up the new location. */
export function __setCharlsWasmUrlForTesting(url: string): void {
  charlsWasmUrl = url;
  charlsModulePromise = null;
}

// Types come from this codebase's own ambient declaration for this package
// (see vite-env.d.ts) — @cornerstonejs/codec-charls ships no .d.ts of its
// own for either entry point.
type CharlsDecodeModule = Awaited<ReturnType<typeof import("@cornerstonejs/codec-charls/decodewasmjs").default>>;

let charlsModulePromise: Promise<CharlsDecodeModule> | null = null;

/** Lazily loads (and caches) the CharLS WASM module — only ever invoked
 *  once a file is already confirmed to be JPEG-LS, so no other transfer
 *  syntax pays for this download/instantiation cost. */
function loadCharlsModule(): Promise<CharlsDecodeModule> {
  if (!charlsModulePromise) {
    charlsModulePromise = (async () => {
      const { default: createCharlsModule } = await import("@cornerstonejs/codec-charls/decodewasmjs");
      return createCharlsModule({ locateFile: () => charlsWasmUrl });
    })();
  }
  return charlsModulePromise;
}

/** Decodes one JPEG-LS bitstream (already reassembled from its DICOM
 *  fragment(s), see `groupFragmentsIntoFrames`) into raw little-endian
 *  pixel bytes. */
async function decodeJpegLsFrame(encoded: Uint8Array): Promise<Uint8Array> {
  const charls = await loadCharlsModule();
  const decoder = new charls.JpegLSDecoder();
  try {
    const input = decoder.getEncodedBuffer(encoded.length);
    input.set(encoded);
    decoder.decode();
    const decoded = decoder.getDecodedBuffer();
    // Copy out of WASM memory — `decoded` is a view that becomes invalid
    // once this decoder instance is deleted (or reused).
    return Uint8Array.from(decoded);
  } finally {
    decoder.delete();
  }
}

// ---- Rebuild ----

function concatFragmentBytes(bytes: Uint8Array, frameFragments: PixelDataFragment[]): Uint8Array {
  if (frameFragments.length === 1) {
    const f = frameFragments[0];
    return bytes.subarray(f.offset, f.offset + f.length);
  }
  const total = frameFragments.reduce((sum, f) => sum + f.length, 0);
  const out = new Uint8Array(total);
  let pos = 0;
  for (const f of frameFragments) {
    out.set(bytes.subarray(f.offset, f.offset + f.length), pos);
    pos += f.length;
  }
  return out;
}

function asciiBytes(str: string): Uint8Array {
  const out = new Uint8Array(str.length);
  for (let i = 0; i < str.length; i++) out[i] = str.charCodeAt(i);
  return out;
}

/** DICOM values must have an even byte length — string VRs pad with a
 *  trailing space or NUL (UI uses NUL, PS3.5 Table 7.1-1). */
function padEven(bytes: Uint8Array, padByte: number): Uint8Array {
  if (bytes.length % 2 === 0) return bytes;
  const out = new Uint8Array(bytes.length + 1);
  out.set(bytes);
  out[bytes.length] = padByte;
  return out;
}

function buildTransferSyntaxElement(uid: string): Uint8Array {
  const value = padEven(asciiBytes(uid), 0x00);
  const out = new Uint8Array(8 + value.length);
  const view = new DataView(out.buffer);
  view.setUint16(0, 0x0002, true);
  view.setUint16(2, 0x0010, true);
  out[4] = "U".charCodeAt(0);
  out[5] = "I".charCodeAt(0);
  view.setUint16(6, value.length, true);
  out.set(value, 8);
  return out;
}

function buildPixelDataElement(vr: "OB" | "OW", value: Uint8Array): Uint8Array {
  const padded = padEven(value, 0x00);
  const out = new Uint8Array(12 + padded.length);
  const view = new DataView(out.buffer);
  view.setUint16(0, 0x7fe0, true);
  view.setUint16(2, 0x0010, true);
  out[4] = vr.charCodeAt(0);
  out[5] = vr.charCodeAt(1);
  view.setUint16(6, 0, true); // reserved
  view.setUint32(8, padded.length, true);
  out.set(padded, 12);
  return out;
}

function concatAll(chunks: Uint8Array[]): ArrayBuffer {
  const total = chunks.reduce((sum, c) => sum + c.length, 0);
  const out = new Uint8Array(total);
  let pos = 0;
  for (const c of chunks) {
    out.set(c, pos);
    pos += c.length;
  }
  return out.buffer;
}

/**
 * Full pre-transcode: decodes every frame of a confirmed JPEG-LS file's
 * PixelData with `@cornerstonejs/codec-charls`, then rebuilds the DICOM
 * byte stream as an ordinary uncompressed (Explicit VR Little Endian)
 * file. Throws with a descriptive message on any failure (malformed input,
 * unsupported fragmentation, a CharLS decode error, ...) — callers should
 * surface that through the same decode-error UI path used for dwv's own
 * decode failures, since a failure here means dwv could never have decoded
 * the file either.
 */
async function rebuildAsUncompressed(bytes: Uint8Array, meta: FileMetaInfo): Promise<ArrayBuffer> {
  const info = readDatasetPixelInfo(bytes, meta.datasetStart);
  const frames = groupFragmentsIntoFrames(info.fragments, info.numberOfFrames);

  const decodedFrames: Uint8Array[] = [];
  for (const frameFragments of frames) {
    const bitstream = concatFragmentBytes(bytes, frameFragments);
    decodedFrames.push(await decodeJpegLsFrame(bitstream));
  }
  const rawPixelBytes =
    decodedFrames.length === 1 ? decodedFrames[0] : new Uint8Array(concatAll(decodedFrames));

  const newTsElement = buildTransferSyntaxElement(UNCOMPRESSED_TRANSFER_SYNTAX_UID);
  const oldTsElementSpan = meta.transferSyntaxElementEnd - meta.transferSyntaxElementStart;
  const delta = newTsElement.length - oldTsElementSpan;
  const newGroupLengthValue = meta.groupLengthValue + delta;
  const newGroupLengthBytes = new Uint8Array(4);
  new DataView(newGroupLengthBytes.buffer).setUint32(0, newGroupLengthValue, true);

  const newPixelDataVr: "OB" | "OW" = info.bitsAllocated > 8 ? "OW" : "OB";
  const newPixelDataElement = buildPixelDataElement(newPixelDataVr, rawPixelBytes);

  const segments = [
    bytes.subarray(0, meta.groupLengthValueOffset),
    newGroupLengthBytes,
    bytes.subarray(meta.groupLengthValueOffset + 4, meta.transferSyntaxElementStart),
    newTsElement,
    bytes.subarray(meta.transferSyntaxElementEnd, info.pixelDataElementStart),
    newPixelDataElement,
    bytes.subarray(info.pixelDataRunEnd, bytes.byteLength),
  ];
  return concatAll(segments);
}

/**
 * Entry point: given a DICOM file's raw bytes, returns bytes dwv can load
 * directly. For every transfer syntax except JPEG-LS (lossless/near-
 * lossless), this returns the ORIGINAL ArrayBuffer completely untouched —
 * no parsing beyond the small file-meta-group read, no copying, no added
 * latency. Only for confirmed JPEG-LS files does it do the (real, CPU/WASM)
 * work of decoding and rebuilding.
 */
export async function transcodeJpegLsIfNeeded(buffer: ArrayBuffer): Promise<ArrayBuffer> {
  const bytes = new Uint8Array(buffer);
  const meta = readFileMeta(bytes);
  if (!meta || !isJpegLsTransferSyntax(meta.transferSyntaxUid)) {
    return buffer;
  }
  return rebuildAsUncompressed(bytes, meta);
}
